import type {
  LandingPageHero,
  LandingPageBenefits,
  LandingPageApproach,
  LandingPageWhatYouGet,
  LandingPageKlartext,
  LandingPagePricingIntro,
  LandingPageFaqItem,
  LandingPageClosing,
  LandingPageWhoTrades,
  LandingPageContent,
} from './types';
import { DEFAULT_APPROACH, DEFAULT_CONTENT, DEFAULT_PRICING_INTRO } from './default';
import svenBolzTeaching from '@/assets/images/sven_bolz_teaching.png';

const DAYTRADING_HERO: LandingPageHero = {
  headline: 'Du handelst bereits Aktien. Jetzt willst du intraday handeln – mit System statt Bauchgefühl.',
  subheadlineLines: [
    'Schau mir börsentäglich zum US-Opening live über die Schulter.',
    'Keine Theorie – sondern echte Entscheidungen – in Echtzeit.',
  ],
  descriptionLines: [
    'Ich zeige dir nicht, was du nach Feierabend lernen sollst.',
    'Ich zeige dir live, wie ich börsentäglich handle – Setups, Einstiege, Risikomanagement.',
    'Während es passiert.',
  ],
  ctaLabel: 'Ich will live dabei sein',
};

const DAYTRADING_BENEFITS: LandingPageBenefits = {
  headline: 'Du kennst das wahrscheinlich:',
  painPoints: [
    'Du handelst an der Börse und würdest gerne „mehr machen" – aber für das kurzfristige Handeln fehlt dir ein klarer Prozess',
    'Du hast das Kapital und das grundsätzliche Verständnis im Kopf – aber im Live-Moment entscheidet dann doch die Emotion oder das Bauchgefühl',
    'Du sitzt bei Daytrading alleine vor dem Chart – und niemand zeigt dir, wie ein Profi in genau diesem Moment entscheidet',
    'Du hast Kurse und Videos gesehen – aber nie jemanden, der wirklich live handelt, statt hinterher zu erklären',
  ],
  conclusion: '…und deshalb bleibt Daytrading für dich ein Thema, das du immer wieder verschiebst.',
  punchline: 'Daytrading macht man nicht mal nebenbei. Es ist Handwerk – und das lernst du nur live.',
};

const DAYTRADING_APPROACH: LandingPageApproach = {
  philosophyLines: DEFAULT_APPROACH.philosophyLines,
  finalLine: DEFAULT_APPROACH.finalLine,
  bridgeLine: DEFAULT_APPROACH.bridgeLine,
  bulletItems: [
    'Wann ist ein Setup wirklich handelbar – und wann ist es nur Wunschdenken?',
    'Wann steigst du sauber ein – und wann lässt du es lieber laufen?',
    'Wann ziehst du die Reißleine – und wann darf eine Position arbeiten?',
  ],
};

const DAYTRADING_WHAT_YOU_GET: LandingPageWhatYouGet = {
  headline: 'Was du hier bekommst:',
  subheadline: 'Keine Theorie – sondern Markt in Echtzeit',
  highlight: 'Mein Live-Trading zum US-Opening, börsentäglich von 15:15 bis 16:15 Uhr – live Tradingideen, live Entscheidungen, live Reaktionen. Du siehst nicht nur das Ergebnis, sondern den Weg dahin.',
  footnote: 'Du musst nicht jeden Tag dabei sein. Zwei, drei Sessions pro Woche reichen, um den Prozess zu verinnerlichen – der Markt öffnet auch morgen wieder.',
};

const DAYTRADING_KLARTEXT: LandingPageKlartext = {
  title: 'Klartext',
  notForYouHeader: 'Das hier ist nichts für dich, wenn…',
  notForYou: '', // ungenutzt – notForYouLines hat Vorrang
  notForYouLines: [
    'du noch keine Erfahrung im Aktienhandel hast – Daytrading ist der nächste Schritt, nicht der erste',
    'du weniger als 50.000 € Kapital für das aktive Handeln einsetzen kannst',
    'du zwischen 15:15 und 16:15 Uhr grundsätzlich nie am Rechner sitzen kannst',
    'du nur konsumieren willst, statt selbst zu handeln',
  ],
  forYouHeader: 'Das hier ist für dich, wenn…',
  forYouLines: [
    'du bereits investierst oder handelst – und intraday endlich mit Prozess statt Bauchgefühl arbeiten willst',
    'du Setups, Einstiege und Risikomanagement live sehen willst – während sie passieren',
    'du bereit bist, regelmäßig eine Stunde zum US-Opening dranzubleiben – um wirklich besser zu werden',
  ],
};

const DAYTRADING_PRICING_INTRO: LandingPagePricingIntro = {
  mainHeadline: DEFAULT_PRICING_INTRO.mainHeadline,
  beforeHeader: 'Du hörst auf:',
  beforeItems: [
    'Daytrading nur in YouTube-Videos zu konsumieren',
    'Trades aus dem Bauch zu öffnen und dann zu hoffen',
    'Theorie zu lesen, ohne sie je live im Markt zu sehen',
  ],
  afterHeader: 'Und fängst an:',
  afterItems: [
    'zum US-Opening live dabei zu sein – wenn die Setups wirklich passieren',
    'Einstiege, Reaktionen und Risikomanagement in Echtzeit zu erleben',
    'selbst sauber zu handeln – mit System statt Bauchgefühl',
  ],
};

const DAYTRADING_FAQ_ITEMS: LandingPageFaqItem[] = [
  {
    question: 'Wie viel Kapital sollte ich mitbringen?',
    answer: 'Ich empfehle mindestens 50.000 € für das aktive Handeln. Darunter wird die Positionsgrößensteuerung zu eng – du kannst Risiko nicht sauber verteilen, und genau das ist der Kern des Handwerks. Wenn du noch nicht so weit bist: Bau erst dein Fundament auf. Der Campus läuft dir nicht weg.',
  },
  {
    question: 'Muss ich jeden Tag dabei sein?',
    answer: 'Nein. Die Sessions laufen börsentäglich von 15:15 bis 16:15 Uhr – aber zwei, drei Sessions pro Woche reichen, um den Prozess zu verinnerlichen. Wichtiger als tägliche Anwesenheit ist Regelmäßigkeit.',
  },
  {
    question: 'Was passiert, wenn ich eine Session verpasse?',
    answer: 'Die Sessions sind live und werden nicht aufgezeichnet – genau das macht sie echt. Verpasst du eine, geht es am nächsten Börsentag weiter.',
  },
  {
    question: 'Ist das ein Signalservice?',
    answer: 'Nein. Ich sage dir nicht, was du kaufen sollst. Ich zeige dir live, wie ich entscheide – damit du lernst, selbst zu entscheiden. Das ist der Unterschied zwischen Nachmachen und Können.',
  },
  {
    question: 'Kann ich jederzeit kündigen?',
    answer: 'Ja, du kannst dein Paket jederzeit zum Ende der Laufzeit kündigen. Es gibt keine versteckten Kosten und keine Mindestvertragslaufzeit über die gewählte Abrechnungsperiode hinaus.',
  },
  {
    question: 'Für wen ist der Börsencampus geeignet?',
    answer: 'Für alle, die bereits Erfahrung mit Aktien haben und den Schritt ins aktive, tägliche Handeln gehen wollen – mit System statt Bauchgefühl. Nicht geeignet für absolute Einsteiger.',
  },
];

const DAYTRADING_CLOSING: LandingPageClosing = {
  sloganLines: [
    { text: 'Der Markt öffnet jeden Tag um', highlight: '15:30 Uhr.' },
    { text: 'Die Frage ist nur: Schaust du zu –', highlight: 'oder bist du dabei?' },
  ],
  ctaLabel: 'Ich will live dabei sein',
};

const DAYTRADING_WHO_TRADES: LandingPageWhoTrades = {
  headline: 'Wer da eigentlich handelt',
  paragraphs: [
    'Ich komme aus dem Banking. Jahrelang habe ich Kunden beraten – und dabei gesehen, wie Banken wirklich Geld verdienen: nicht mit Prognosen, sondern mit Wahrscheinlichkeiten.',
    'Seit 2019 handle ich hauptberuflich. Nicht als Hobby, nicht nebenbei – börsentäglich, mit eigenem Kapital und festem Prozess.',
    'Genau diesen Prozess siehst du in den Live-Sessions. Ungeschnitten, in Echtzeit, mit allem, was dazugehört – auch den Trades, die nicht aufgehen.',
  ],
  imageSrc: svenBolzTeaching,
  imageAlt: 'Sven Bolz beim Live-Trading',
};

export const DAYTRADING_CONTENT: LandingPageContent = {
  ...DEFAULT_CONTENT,
  hero: DAYTRADING_HERO,
  benefits: DAYTRADING_BENEFITS,
  approach: DAYTRADING_APPROACH,
  whatYouGet: DAYTRADING_WHAT_YOU_GET,
  klartext: DAYTRADING_KLARTEXT,
  pricingIntro: DAYTRADING_PRICING_INTRO,
  faqItems: DAYTRADING_FAQ_ITEMS,
  calculatorDefaultCapital: 50_000,
  calculatorNote: 'Empfohlenes Mindestkapital für aktives Daytrading: 50.000 €. Darunter auch möglich, aber weniger effizient.',
  tradeTableNote: 'Auch wenn du hier evtl. nur grüne Tage siehst: auch rote Tage gehören dazu. Entscheidend ist nicht der einzelne Tag – sondern dass die Wahrscheinlichkeit über viele Trades auf deiner Seite steht. Genau das siehst du hier live.',
  senjaWidgetId: '0b66dcc3-3b1d-4b5e-a50a-55644fecf084',
  heroVideoUrl: 'https://iframe.mediadelivery.net/embed/491441/cdf27c75-6e3c-4405-9543-0080ee416c0c?autoplay=true&captions=true&muted=true',
  hideTestimonials: false,
  testimonialsHeadline: 'Das sagen Trader, die live dabei sind',
  testimonialsSubline: 'Echte Stimmen aus den täglichen Sessions',
  faqCtaLabel: 'Ich will live dabei sein',
  closing: DAYTRADING_CLOSING,
  whoTrades: DAYTRADING_WHO_TRADES,
};
